<template>
    <div
        id="app-theme-root"
        :data-theme="localConfig.theme"
        class="w-dvw h-dvh"
        :class="isAuthLayout ? 'bg-surface-secondary' : 'bg-surface-primary'"
    >
        <div class="safe-area w-dvw h-dvh overflow-y-auto overflow-x-hidden">
            <NuxtLayout>
                <NuxtPwaAssets />
                <NuxtPage />
            </NuxtLayout>
        </div>
    </div>

    <!-- Global container for teleported alerts/banners -->
    <div
        id="global-alert-stack"
        :data-theme="localConfig.theme"
        class="fixed top-0 left-1/2 -translate-x-1/2 w-full md:w-[600px] md:max-w-[600px] z-[10001] pointer-events-none bg-transparent flex flex-col gap-2 p-4 pt-[calc(max(var(--safe-area-inset-top,0px),1rem))] md:pt-4"
    ></div>
</template>

<script setup lang="ts">
import { SafeArea, type SafeAreaInsets } from "capacitor-plugin-safe-area";
import { Keyboard } from "@capacitor/keyboard";
import { useUserStore } from "@/store/user";
import { useRemoteConfigStore } from "@/store/remoteConfig";
import { useLocalConfigStore } from "@/store/localConfig";

const localConfig = useLocalConfigStore();
const { IS_DEV_ENV } = useRuntimeConfig().public;
const { isiOS, isAndroid } = usePlatform();
const route = useRoute();

const isAuthLayout = computed(
    () => route.meta.layout === "auth-new",
);

// Hide the iOS keyboard accessory bar (Done button/chevrons)
if (isiOS.value) {
    Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => {
        if (IS_DEV_ENV) {
            console.log("Failed to hide keyboard accessory bar");
        }
    });
}

// Do not comment this out as settings (lang, theme) won't apply.
const remoteConfig = useRemoteConfigStore();
useHead({
    titleTemplate: (titleChunk) => {
        return titleChunk
            ? `${titleChunk} - ` + remoteConfig.appName
            : remoteConfig.appName;
    },
});


const { name, email, id, intercomJwtToken } = storeToRefs(useUserStore());
const { $intercom } = useNuxtApp();

if (!isAndroid.value) {
    SafeArea.getSafeAreaInsets().then(setSafeAreaInsets);
    await SafeArea.addListener("safeAreaChanged", setSafeAreaInsets);
}

function setSafeAreaInsets(data: SafeAreaInsets) {
    const { insets } = data;
    for (const [key, value] of Object.entries(insets)) {
        document.documentElement.style.setProperty(
            `--safe-area-inset-${key}`,
            `${value}px`,
        );
    }
}

const baseUrl = useRuntimeConfig().public.njordaApiUrl;

if (IS_DEV_ENV) {
    console.log("Njorda API server", baseUrl);
}

watchEffect(() => {
    const updateIntercom: Record<string, string> = {};
    if (name.value) {
        updateIntercom.name = name.value;
    }
    if (email.value) {
        updateIntercom.email = email.value;
    }
    if (id.value) {
        updateIntercom.user_id = id.value;
    }
    if (intercomJwtToken.value) {
        updateIntercom.intercom_user_jwt = intercomJwtToken.value;
    }
    if (intercomJwtToken.value && Object.keys(updateIntercom).length > 0) {
        $intercom.update(updateIntercom);
    }
});
</script>
<!-- Temporary fix - Moves intercom icon above the navigation bar in the mobile view  -->
<style>
@media only screen and (max-width: 800px) {
    .intercom-lightweight-app-launcher {
        bottom: 120px !important;
        right: 20px !important;
        left: unset !important;
    }
}
</style>
